import type { OptionType } from "components/inputs";
import { Select } from "components/inputs";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setSelectedFileDoi } from "reducers/future-medical";
import type { AmendedReviewScreeningType, FileType } from "types/DataTypes";

import { useFileDOI } from "./useFileDOI";

type DOIFileHeaderSelectProps = {
  file?: FileType;
  service?: AmendedReviewScreeningType;
};

/* this is used for Amended Review Screening, since it's a file-based DOI (aka date-of-injury not service-date-of-injury) */
export const DOIFileHeaderSelect = ({
  file,
  service,
}: DOIFileHeaderSelectProps) => {
  const dispatch = useDispatch();
  const fileToUse = service?.file || file;

  const { dois, selectedDoiId, selectDoi, isLoading, isSuccess } =
    useFileDOI(fileToUse);

  // reset selectedFileDoi when component unmounts
  useEffect(() => {
    return () => {
      dispatch(setSelectedFileDoi(null));
    };
  }, [dispatch]);

  // automatically select the first DOI once data loads
  useEffect(() => {
    if (isSuccess && dois?.length && !selectedDoiId && !isLoading) {
      selectDoi(dois[0].id);
    }
  }, [dois, isSuccess, isLoading, selectedDoiId, selectDoi]);

  const options = useMemo(() => {
    if (!dois) return [];

    return dois.map((doi) => {
      return {
        label: doi.date.substring(0, 10),
        value: doi.id,
      };
    });
  }, [dois]);

  const selectedOption = useMemo(
    () =>
      options.find(
        (option) => option.value?.toString() === selectedDoiId?.toString(),
      ),
    [options, selectedDoiId],
  );

  if (isLoading) {
    return <div className="doi-file-header-select">Loading...</div>;
  }

  if (!dois?.length) {
    return <div className="doi-file-header-select">No dates available</div>;
  }

  return (
    <div className="doi-file-header-select">
      <Select
        className="kaboodle__control"
        onChange={(o: OptionType) => selectDoi(o?.value?.toString() || null)}
        name="doi"
        value={selectedOption}
        options={options}
        isClearable={false}
        isSearchable={false}
      />
    </div>
  );
};
